import React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon, XCircleIcon, ClockIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { ApproveReportDialog } from "./approveReportDialog";
import { useUserHasAccess } from "../../../../routes/roleOnly";

export const ReportApprovalSection = ({
  report,
  onRefresh,
  openProcessTrigger,
  className = "",
  hideCard = false,
}) => {
  const { t } = useTranslation();
  const [approveDialogOpen, setApproveDialogOpen] = React.useState(false);
  const canManageReports = useUserHasAccess({ role: "service", action: "manage" });
  const canApproveReports = useUserHasAccess({ role: "service", action: "approve" }) || canManageReports;

  React.useEffect(() => {
    if (openProcessTrigger) {
      setApproveDialogOpen(true);
    }
  }, [openProcessTrigger]);

  if (!report?.finished) {
    return null;
  }

  const getStatusInfo = () => {
    if (report?.approved) {
      return {
        icon: CheckCircleIcon,
        bgColor: "bg-green-100",
        iconColor: "text-green-600",
        textColor: "text-green-900",
        borderColor: "border-green-200",
        statusText: t("Godkjent"),
        description: t("Rapporten er godkjent"),
      };
    } else if (report?.not_approved) {
      return {
        icon: XCircleIcon,
        bgColor: "bg-red-100",
        iconColor: "text-red-600",
        textColor: "text-red-900",
        borderColor: "border-red-200",
        statusText: t("Ikke godkjent"),
        description: report?.process_note || t("Rapporten må korrigeres"),
      };
    } else if (report?.updated_after_process) {
      return {
        icon: ExclamationTriangleIcon,
        bgColor: "bg-yellow-100",
        iconColor: "text-yellow-600",
        textColor: "text-yellow-900",
        borderColor: "border-yellow-200",
        statusText: t("Oppdatert"),
        description: t("Rapporten er oppdatert og klar for behandling på nytt"),
      };
    } else {
      return {
        icon: ClockIcon,
        bgColor: "bg-blue-100",
        iconColor: "text-blue-600",
        textColor: "text-blue-900",
        borderColor: "border-blue-200",
        statusText: t("Venter på behandling"),
        description: t("Rapporten er ferdigstilt og venter på godkjenning"),
      };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <>
      {!hideCard ? (
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className={`bg-gradient-to-r ${statusInfo.bgColor} px-6 py-4 border-b ${statusInfo.borderColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 ${statusInfo.bgColor} rounded-lg flex items-center justify-center`}>
                  <StatusIcon className={`w-6 h-6 ${statusInfo.iconColor}`} />
                </div>
              </div>
              <div className="ml-4">
                <div className="flex items-center space-x-3">
                  <h3 className={`text-lg font-semibold ${statusInfo.textColor}`}>
                    {t("Behandling av rapport")}
                  </h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                    {statusInfo.statusText}
                  </span>
                </div>
                <p className={`text-sm ${statusInfo.textColor} opacity-80`}>
                  {report?.not_approved
                    ? t("service.report.correction.subtitle")
                    : statusInfo.description}
                </p>
                {report?.process_note && report?.approved && (
                  <p className={`text-sm ${statusInfo.textColor} opacity-70 mt-1 italic`}>
                    {t("Notat")}: {report.process_note}
                  </p>
                )}
              </div>
            </div>
            {(canManageReports || canApproveReports) && (
              <div className="flex space-x-2">
                <button
                  onClick={() => setApproveDialogOpen(true)}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    report?.approved
                      ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                      : report?.not_approved
                      ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                      : report?.updated_after_process
                      ? "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
                      : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
                  }`}
                >
                  <StatusIcon className="w-4 h-4 mr-2" />
                  {t("Behandle rapport")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      ) : null}

      <ApproveReportDialog
        open={approveDialogOpen}
        report={report}
        onClose={() => setApproveDialogOpen(false)}
        onRefresh={onRefresh}
      />
    </>
  );
};

